import Head from 'next/head';
import { request } from 'graphql-request';
import dynamic from 'next/dynamic';

import { getYouTubeVideosFromPlaylist } from '~/utils/youtube.server';
import { get, getStructuredData, getContentPaths, getBlocksFromQueue, getAlias } from '~/utils/utils';
import BlockParser from '~/components/blockParser/blockParser';
import PageWrapper from '~/components/wrappers/pageWrapper';
import SiteSettings from '~/siteSettings';

// GQL
import GET_CONTENT_HUB from '~/graphql/GET_CONTENT_HUB.graphql';

const Utag = dynamic(() => import('~/components/utag/utag'), { ssr: false });
const BidBarrelReact = dynamic(() => import('~/components/bidBarrelReact/bidBarrelReact'), { ssr: false });

/**
 * Content Hubs
 * @category Page
 * @extends Component
 * @summary Display the content hub page.
 * @return {Node} React node containing the content hub page.
 */

const ContentHub = props => {
  const { router, sitePrimaryRsid, baseUrl, data, blocks, latestVideos } = props;
  const path = get(router, 'query.alias')
    ? `/topics/${ router.query.alias.join('/') }`
    : '/';

  const content = {
    ...data.nodeQuery
  };

  const keywords = content.field_tags && content.field_tags.map(tag => tag.name).join(',');

  return <>
    <Head>
      <title>{ content.title } | { SiteSettings.siteName }</title>
      <meta name='title' content={ content.title } />
      <link rel='canonical' href={`${ baseUrl }${ path }`} />
      <meta name='twitter:card' content='summary_large_image' />
      <meta name='twitter:title' content={ content.field_social_headline || content.title } />
      <meta name='description' content={ content.field_seo_description || content.title } />
      <meta property='og:url' content={ path } />
      <meta property='og:title' content={ content.field_social_headline || content.title } />
      <meta property='og:image' content={ content?.field_category?.field_image?.field_image?.social?.url } />
      <meta property='og:description' content={ content.field_social_description || content.title } />
      <link rel='image_src' href={ content?.field_category?.field_image?.field_image?.social?.url } />
      <meta name='twitter:url' content={ path } />
      <meta name='twitter:image:width' content={ content?.field_category?.field_image?.field_image?.social?.width } />
      <meta name='twitter:image:height' content={ content?.field_category?.field_image?.field_image?.social?.height } />
      <meta name='twitter:image' content={ content?.field_category?.field_image?.field_image?.social?.url } />
      <meta property='article:published_time' content={ content.field_display_date } />
      <meta name='keywords' content={ keywords || SiteSettings.defaultKeywords } />
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(getStructuredData(baseUrl, path, content)) }}
      />
    </Head>
    <div className='content-hub__hero'>
      <div className='content-hub__hero-img'>
        { content?.field_category?.field_image?.field_image?.lg?.url ?
          <picture>
            <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet={ content.field_category.field_image.field_image.sm.url } />
            <img
              src={ content.field_category.field_image.field_image.lg.url }
              alt={ content.field_image?.alt }
              width={ content.field_category.field_image.field_image.lg.width }
              height={ content.field_category.field_image.field_image.lg.height }
              loading='lazy'
              className='img-full'
            />
          </picture>
          :
          <picture>
            <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet='/img/375x540_fpo.jpg' />
            <img
              src='/img/1440x558_fpo.jpg'
              alt={ content.title }
              width='1440'
              height='540'
              loading='lazy'
              className='img-full'
            />
          </picture>
        }
      </div>
      <div className='content-hub__hero-desc'>
        <h1>{ content.title }</h1>
      </div>
    </div>
    { blocks?.entitySubQueueQuery?.items &&
      <BlockParser
        blockItems={ blocks.entitySubQueueQuery.items }
        latestVideos={ latestVideos }
      />
    }
    <Utag
      pageType='content hub'
      siteSection={ content.title }
      data={{
        sitePrimaryRsid,
        assetType: 'content hub',
        ...(keywords && {keywords})
      }}
    />
  </>;
}

export async function getStaticPaths() {
  const paths = await getContentPaths('/topics/', {
    machine_name: 'latest_content_by_type',
    display: 'master',
    itemsPerPage: parseInt(process.env.PRERENDER_COUNT, 10) || 10,
    offset: 0,
    viewArgs: [
      'content_hub'
    ]
  });

  return {
    paths,
    fallback: 'blocking'
  };
}

export async function getStaticProps(ctx) {
  const aliasData = await getAlias({
    alias: `/topics/${ ctx.params.alias.join('/') }`
  });

  if(!aliasData?.pathAliasQuery?.nid) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  // Get Data
  const data = await request(process.env.NEXT_PUBLIC_API_HOST || 'https://api-stage.thedrewbarrymoreshow.com/cmv_graphql/api/query', GET_CONTENT_HUB, {
    target_id: parseInt(aliasData.pathAliasQuery.nid, 10)
  });

  if(!data?.nodeQuery) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  // Get Blocks
  let blocks = [];
  if(data?.nodeQuery?.field_queue?.name) {
    blocks = await getBlocksFromQueue({
      target_id: data?.nodeQuery?.field_queue?.name,
      limit: 100
    });
  }

  // YouTube playlist IDs must be mapped for specific aliases as required
  let latestVideos = [];
  if (ctx.params.alias[0] === 'recipes-cooking') {
    // Get Drew's "Recipes and Cooking Tutorials" playlist
    latestVideos = await getYouTubeVideosFromPlaylist(process.env.YOUTUBE_PLAYLIST_ID_RECIPES_COOKING);
  }

  return {
    props: {
      data,
      blocks,
      latestVideos
    },
    revalidate: 1
  }
}

export default PageWrapper(ContentHub);
